import React from 'react';
import { LoadingState as SharedLoadingState } from '../Spinner';
import { AccentBar } from '../ui/deck';

// Shared presentational primitives for the Analytics suite. Kept dependency-free
// (Tailwind only) so every analytics page has a consistent, modern look.
// Dark mode follows the deck (pitch) styling: flat navy panels, no drop shadows,
// thin pink accents.

export const Card: React.FC<
  {
    className?: string;
    children: React.ReactNode;
  } & React.HTMLAttributes<HTMLDivElement>
> = ({ className = '', children, ...rest }) => (
  <div
    className={`bg-white dark:bg-deck-panel rounded-2xl shadow-sm dark:shadow-none ring-1 ring-gray-100 dark:ring-white/10 ${className}`}
    {...rest}
  >
    {children}
  </div>
);

export const SectionTitle: React.FC<{
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, subtitle, icon, action }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div className="flex items-center gap-2">
      {icon && <span className="text-pink-500">{icon}</span>}
      <div>
        <AccentBar className="mb-2" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-deck-mute">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

export const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  icon?: React.ReactNode;
  accent?: string; // tailwind text color class for the value
  onClick?: () => void;
}> = ({ label, value, sublabel, icon, accent = 'text-pink-600 dark:text-pink-400', onClick }) => (
  <Card
    className={`p-5${onClick ? ' cursor-pointer transition-shadow hover:ring-2 hover:ring-pink-400 dark:hover:ring-pink-500 focus-within:ring-2 focus-within:ring-pink-400' : ''}`}
    {...(onClick
      ? {
          role: 'button',
          tabIndex: 0,
          onClick,
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          },
        }
      : {})}
  >
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-500 dark:text-deck-mute">{label}</span>
      {icon && <span className="text-gray-300 dark:text-deck-faint text-lg">{icon}</span>}
    </div>
    <div className={`mt-2 text-3xl font-bold tracking-tight ${accent}`}>{value}</div>
    {sublabel && (
      <div className="mt-1 text-xs text-gray-400 dark:text-deck-faint">{sublabel}</div>
    )}
  </Card>
);

export const ProgressBar: React.FC<{
  value: number; // 0..100
  colorClass?: string;
  trackClass?: string;
  className?: string;
}> = ({ value, colorClass = 'bg-pink-500', trackClass = 'bg-gray-100 dark:bg-white/10', className = '' }) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className={`w-full h-2.5 rounded-full overflow-hidden ${trackClass} ${className}`}>
      <div
        className={`h-full rounded-full transition-all duration-700 ease-out ${colorClass}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

export const LoadingState: React.FC<{ label?: string }> = ({ label = 'Loading analytics…' }) => (
  <SharedLoadingState label={label} />
);

export const ErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <Card className="p-8 text-center">
    <p className="text-red-500 dark:text-red-400 font-medium">{message}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-[#FF3EB5] hover:bg-[#E635A3] text-white text-sm font-medium transition-colors"
      >
        Try again
      </button>
    )}
  </Card>
);

export const EmptyState: React.FC<{ title: string; hint?: string; icon?: React.ReactNode }> = ({ title, hint, icon }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    {icon && <div className="text-4xl text-gray-300 dark:text-deck-faint mb-3">{icon}</div>}
    <p className="text-gray-600 dark:text-deck-mute font-medium">{title}</p>
    {hint && <p className="mt-1 text-sm text-gray-400 dark:text-deck-faint max-w-md">{hint}</p>}
  </div>
);

export const Pill: React.FC<{
  children: React.ReactNode;
  colorClass?: string;
}> = ({ children, colorClass = 'bg-pink-50 text-pink-600 dark:bg-pink-900/40 dark:text-pink-300' }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
    {children}
  </span>
);
